import { Component, OnInit, Input, Output, TemplateRef, EventEmitter, ViewChild } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { PluginsService } from '../../plugins/plugins.service';
import { PaymentService } from '../../payment/payment.service';
import { SessionsService } from '../../sessions/sessions.service';
import { ISession } from '../../../models/session';
import { IPlugin } from '../../../models/plugin';
import { IAudience } from '../../../models/audience';
import { filter, propEq, pluck, sum, contains } from 'ramda/es';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';

const keepActivatedPluginsForSession = (plugins: IPlugin[], sessionType) => {
  return filter(plugin => {
    return contains(sessionType, plugin.sessionTypes) && propEq('isActivated', true)(plugin);
  })(plugins);
};

const SETUP_COSTS = 3;
// For now, setup costs must support multiplicator but it may change in the futur
// Ask for VR for more informations about that
export const getSetupCosts = (multiplicator: number) => {
  return (SETUP_COSTS * multiplicator);
}

const getAccessCosts = sessionTypes => {
  if ('free' === sessionTypes.accountType && 'public' === sessionTypes.privacy) {
    // free access
    return { value: 0, labelKey: 'sessions.edit.live_mode.activation.access_costs_free'};
  } else if ('registration' === sessionTypes.accountType && 'public' === sessionTypes.privacy) {
    // registration access
    return { value: 5, labelKey: 'sessions.edit.live_mode.activation.access_costs_restricted'};
  } else if ('invitation' === sessionTypes.accountType && 'private' === sessionTypes.privacy) {
    // autologin access
    return { value: 3, labelKey: 'sessions.edit.live_mode.activation.access_costs_invitation'};
  } else if ('generic_login' === sessionTypes.accountType && 'private' === sessionTypes.privacy) {
    // code access
    return { value: 1, labelKey: 'sessions.edit.live_mode.activation.access_costs_code'};
  } else {
    return { value: 5, labelKey: 'sessions.edit.live_mode.activation.access_costs_restricted'};
  }
}

export const getAmountToCredit = (sessionTypes, plugins: IPlugin[], multiplicator): number => {
  const { value } = getAccessCosts(sessionTypes);
  return Math.ceil(
    value +
    getSetupCosts(multiplicator) +
      sum(pluck('credits')(keepActivatedPluginsForSession(plugins, sessionTypes.sessionType))) * multiplicator
  );
};

@Component({
  selector: 'live-mode',
  templateUrl: './live-mode.component.html',
  styles: [
    `
      .modal-header {
        padding: 10px;
      }
      .live-mode-container {
        padding: 30px;
        margin-bottom: 15px;
        text-align: center;
        background-color: #fff;
      }
      .warning {
        margin-bottom: 3px;
      }
      .activate-button {
        font-weight: 600;
      }
      .title {
        font-weight: 600;
      }
      .warn-message {
        color: #f18624;
        font-weight: 600;
      }
      td:nth-child(2) {
        text-align: center;
      }
      .credits-summary {
        font-weight: 600;
      }
      .not-enough-credits {
        color: #e74c3c;
        font-weight: 600;
        margin-bottom: 10px;
      }
      .controls-button {
        margin-top: 20px;
      }
      .inline {
        display: inline-block;
        vertical-align: middle;
      }
      .helper {
        margin-top: 20px !important;
      }
      .helper2 {
        color: darkorange;
      }
      .reset-session-group {
        background-color: #f0f0f0;
        padding: 5px;
      }
    `
  ]
})
export class LiveModeComponent implements OnInit {
  @Input() session: ISession;
  @Input() mode: 'normal' | 'slim' = 'normal';
  @Output() multiplicatorChange: EventEmitter<number> = new EventEmitter();
  @ViewChild('confirmModal') confirmModal: TemplateRef<any>;
  @ViewChild('confirmApplyModal') confirmApplyModal: TemplateRef<any>;

  public pluginsToCredit$: Observable<IPlugin[]>;
  public modalRef: BsModalRef;
  public applyModalRef: BsModalRef;
  public amountToCredit: number;
  public audience: IAudience = { value: 0, multiplicator: 1 };
  public audienceValue: number;
  public setupCosts = getSetupCosts(1);
  public accessCosts = { value: 0, labelKey: 'sessions.edit.live_mode.activation.access_costs_free' };
  public showPremiumButton: boolean = false;
  public resetSession: boolean = false;

  constructor(
    private modalService: BsModalService,
    private pluginsService: PluginsService,
    private sessionsService: SessionsService,
    public paymentService: PaymentService
  ) {}

  ngOnInit() {
    this.pluginsToCredit$ = this.pluginsService.getAll(this.session).pipe(
      map((plugins: IPlugin[]) => {
        return keepActivatedPluginsForSession(plugins, this.session.types.sessionType);
      })
    );

    this.pluginsToCredit$.subscribe(plugins => {
      this.amountToCredit = getAmountToCredit(
        this.session.types,
        plugins,
        this.audience.multiplicator
      );
    });

    this.audienceValue = this.sessionsService.getAudience(this.session);
    if (this.audienceValue > 0) {
      this.showPremiumButton = true;
    }

    this.accessCosts = getAccessCosts(this.session.types);
  }

  openModal(template: TemplateRef<any>) {
    this.ngOnInit();
    this.modalRef = this.modalService.show(template, Object.assign({}, { class: 'inmodal' }));
  }

  confirm() {
    this.paymentService
      .decrease(this.amountToCredit)
      .flatMap(() =>
        this.sessionsService.update(
          this.session.id,
          Object.assign(this.session, {
            isPremium: true,
            maxusers: this.audience.value
          })
        )
      )
      .flatMap(() => this.sessionsService.updateStatus(this.session.id, 'BOOKED'))
      .flatMap(() => this.resetSession ? this.sessionsService.reset(this.session.id) : Observable.of(null))
      .subscribe(() => this.modalRef.hide());
  }

  onMultiplicatorChange(audience: IAudience) {
    this.sessionsService.setAudience(this.session, audience.value);
    this.audience = audience;
    this.showPremiumButton = true;
    this.setupCosts = getSetupCosts(audience.multiplicator);
    this.multiplicatorChange.emit(audience.multiplicator);
  }

  apply() {
    this.applyModalRef = this.modalService.show(this.confirmApplyModal, Object.assign({}, { class: 'inmodal' }));
  }

}



// WEBPACK FOOTER //
// ./src/app/modules/shared/live-mode/live-mode.component.ts